import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ButtonComponent, SpinnerComponent } from 'ui-library';
import { Observable, Subject, takeUntil } from 'rxjs';
import { toError } from 'src/app/classes/shared/logs';
import { Pop } from 'src/app/classes/shared/pop';
import { Attachment } from 'src/app/interfaces/api/attachment';
import { Video } from 'src/app/interfaces/api/video';
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { TranslateModule } from '@ngx-translate/core';

interface VideoCaptureData {
  videoUrl: string;
  createdDate?: string;
  file?: File;
  thumbnail?: string;
  tracker?: string;
  trackerName?: string;
  event?: string;
  camera?: string;
  uploadState$?: Observable<{ uploading: boolean; videoSrc?: string } | null>;
  autoDone?: boolean;
  onCaptureMany: (frames: { base64: string; createdDate: string }[], isAuto?: boolean) => void;
  onUploaded?: (attachment: Attachment, video: Video) => void;
}

@Component({
  selector: 'app-video-capture',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressBarModule,
    ButtonComponent,
    SpinnerComponent,
    TranslateModule,
  ],
  templateUrl: './video-capture.component.html',
  styleUrl: './video-capture.component.scss',
})
export class VideoCaptureComponent implements OnInit, OnDestroy {
  private readonly data = inject<VideoCaptureData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<VideoCaptureComponent>);
  private readonly attachmentService = inject(AttachmentService);
  private readonly pop = inject(Pop);
  private readonly destroy$ = new Subject<void>();

  @ViewChild('videoEl') videoEl!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasEl') canvasEl!: ElementRef<HTMLCanvasElement>;

  videoUrl = this.data.videoUrl;
  isAutoExtracting = false;
  isUploading = false;
  uploaded = false;
  autoDone = this.data.autoDone ?? false;
  extractionProgress: { current: number; total: number } | null = null;

  private localBlobUrl: string | null = null;
  private captureVideoEl: HTMLVideoElement | null = null;

  ngOnInit(): void {
    this.data.uploadState$?.pipe(takeUntil(this.destroy$)).subscribe(state => {
      if (!state) return;
      this.isUploading = state.uploading;
      if (!state.uploading && state.videoSrc) {
        this.uploaded = true;
      }
    });
  }

  private frameDate(seconds: number): string {
    const start = this.data.createdDate ? Date.parse(this.data.createdDate) : NaN;
    if (isNaN(start)) return new Date().toISOString();
    return new Date(start + seconds * 1000).toISOString();
  }

  async capture(): Promise<void> {
    const currentTime = this.videoEl.nativeElement.currentTime;
    const canvas = this.canvasEl.nativeElement;
    try {
      const v = await this.getCaptureVideo();
      await this.seekTo(v, currentTime);
      canvas.width = v.videoWidth;
      canvas.height = v.videoHeight;
      canvas.getContext('2d')!.drawImage(v, 0, 0);
      this.data.onCaptureMany([{ base64: canvas.toDataURL('image/jpeg', 0.9), createdDate: this.frameDate(currentTime) }]);
    } catch {
      this.pop.toast('Capture impossible depuis cette source vidéo', 'error');
    }
  }

  async toggleAutoExtract(): Promise<void> {
    if (this.isAutoExtracting) {
      this.isAutoExtracting = false;
      return;
    }

    this.isAutoExtracting = true;
    const duration = this.videoEl.nativeElement.duration;
    const canvas = this.canvasEl.nativeElement;
    const ctx = canvas.getContext('2d')!;

    const times: number[] = [];
    for (let t = 0; t < duration; t += 5) times.push(t);
    this.extractionProgress = { current: 0, total: times.length };

    const rawFrames: string[] = [];

    try {
      const v = await this.getCaptureVideo();
      canvas.width = v.videoWidth || 1280;
      canvas.height = v.videoHeight || 720;
      for (let i = 0; i < times.length; i++) {
        if (!this.isAutoExtracting) break;
        await this.seekTo(v, times[i]!);
        if (!this.isAutoExtracting) break;
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
        rawFrames.push(canvas.toDataURL('image/jpeg', 0.9));
        this.extractionProgress = { current: i + 1, total: times.length };
      }
    } catch {
      this.pop.toast('Capture impossible depuis cette source vidéo', 'error');
    }

    const completed = rawFrames.length === times.length;
    this.isAutoExtracting = false;
    this.extractionProgress = null;

    if (!rawFrames.length) return;

    const frames = rawFrames.map((base64, i) => ({
      base64,
      createdDate: this.frameDate(times[i]!),
    }));

    if (completed) this.autoDone = true;
    this.data.onCaptureMany(frames, completed);
  }

  get canUpload(): boolean {
    return !!this.data.file && !this.uploaded;
  }

  // Returns a video element suitable for canvas drawing.
  // For blob URLs (same-origin) the main video element is used directly.
  // For cross-origin Azure URLs the video is fetched once as a blob, cached,
  // and a persistent off-screen video element is created from the local blob URL.
  private async getCaptureVideo(): Promise<HTMLVideoElement> {
    if (this.videoUrl.startsWith('blob:')) {
      return this.videoEl.nativeElement;
    }

    if (!this.captureVideoEl) {
      if (!this.localBlobUrl) {
        const response = await fetch(this.videoUrl);
        const blob = await response.blob();
        this.localBlobUrl = URL.createObjectURL(blob);
      }
      const v = document.createElement('video');
      v.src = this.localBlobUrl;
      v.muted = true;
      await new Promise<void>(r => { v.onloadedmetadata = () => r(); });
      this.captureVideoEl = v;
    }

    return this.captureVideoEl;
  }

  private seekTo(video: HTMLVideoElement, time: number): Promise<void> {
    return new Promise(res => {
      video.addEventListener('seeked', () => res(), { once: true });
      video.currentTime = time;
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.captureVideoEl?.remove();
    if (this.localBlobUrl) URL.revokeObjectURL(this.localBlobUrl);
  }
}
